import { CommonModule, Location } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  RendererFactory2,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import * as L from 'leaflet';
import { AuthenticationService } from '../../services/authentication.service';
import { UsersService } from '../../services/users.service';
import { RegisterService } from '../../services/register.service';
import { StepsComponent } from '../../components/steps/steps.component';
import { MessagePopupComponent } from '../../modals/message-popup/message-popup.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import AccountTypes from '../../models/account_types.json';
import Plans from '../../models/plans.json';
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import { TranslateService } from '@ngx-translate/core';

@Component({
  standalone: false,
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
})
export class RegisterComponent implements OnInit, OnDestroy, AfterViewInit {
  accountTypes: any = AccountTypes;
  pricingPlans: any = Plans;
  filteredPricingPlans: any = Plans;
  steps = [
    {
      id: 1,
      title: 'account',
    },
    {
      id: 2,
      title: 'location',
    },
    {
      id: 3,
      title: 'type',
    },
    {
      id: 4,
      title: 'plan',
    },
  ];
  currentStep;
  accountForm: FormGroup;
  locationForm: FormGroup;
  typeForm: FormGroup;
  planForm: FormGroup;
  inputType1 = 'password';
  showHide1 = '../../../../assets/hide.svg';
  inputType2 = 'password';
  showHide2 = '../../../../assets/hide.svg';
  mymap: L.Map;
  marker;
  selectedType;
  selectedPlan;
  isError = false;
  isErrorText = 'form-is-not-valid';
  disableButton = false;
  @ViewChild(StepsComponent) stepsComponent: StepsComponent;

  constructor(
    private router: Router,
    private location: Location,
    private authenticationService: AuthenticationService,
    private usersService: UsersService,
    private registerService: RegisterService,
    private modalService: BsModalService,
    private fb: FormBuilder,
    private translateService: TranslateService,
  ) {}

  ngOnInit() {
    const user = this.authenticationService.userValue;
    if (user) {
      this.router.navigate(['/dashboard']);
    }
    this.isError = false;
    this.isErrorText = 'form-is-not-valid';

    this.initForms();
  }

  ngAfterViewInit(): void {
    this.getUserLocation();

    this.stepsComponent.currentStepChange.subscribe(() => {
      this.getUserLocation();
    });
  }

  ngOnDestroy() {
    if (this.mymap) {
      this.mymap.off();
      this.mymap.remove();
    }
  }

  initForms() {
    this.accountForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required],
      phone: ['', Validators.required],
    });

    this.locationForm = this.fb.group({
      display_name: ['', Validators.required],
      name: [
        '',
        [
          Validators.required,
          Validators.pattern(/^[a-z0-9]+(?:[._-][a-z0-9]+)*$/),
        ],
      ],
      locationLatitude: [null, Validators.required],
      locationLongitude: [null, Validators.required],
    });

    this.locationForm.get('name').valueChanges.subscribe((value: string) => {
      if (!value) {
        return;
      }
      const formatted = value.toLowerCase().replace(/ /g, '_');
      if (formatted !== value) {
        this.locationForm.get('name').setValue(formatted, { emitEvent: false });
      }
    });

    this.typeForm = this.fb.group({
      selectedType: [null, Validators.required],
    });

    this.planForm = this.fb.group({
      selectedPlan: [null, Validators.required],
    });
  }

  goToLogin() {
    this.router.navigate(['/login']);
  }

  showHideField(numri: any) {
    if (numri === 1) {
      if (this.inputType1 === 'password') {
        this.inputType1 = 'text';
        this.showHide1 = '../../../../assets/show.svg';
      } else {
        this.inputType1 = 'password';
        this.showHide1 = '../../../../assets/hide.svg';
      }
    }
    if (numri === 2) {
      if (this.inputType2 === 'password') {
        this.inputType2 = 'text';
        this.showHide2 = '../../../../assets/show.svg';
      } else {
        this.inputType2 = 'password';
        this.showHide2 = '../../../../assets/hide.svg';
      }
    }
  }

  setSelectedType(type) {
    this.selectedType = type;
    this.typeForm.get('selectedType').setValue(type);
  }

  setSelectedPlan(plan) {
    this.selectedPlan = plan;
    this.planForm.get('selectedPlan').setValue(plan);
  }

  goBack() {
    if (!this.currentStep || this.currentStep.id === this.steps[0].id) {
      this.location.back();
    } else if (this.currentStep.id === this.steps[1].id) {
      this.stepsComponent.setCurrentStep(this.steps[0]);
    } else if (this.currentStep.id === this.steps[2].id) {
      this.stepsComponent.setCurrentStep(this.steps[1]);
      setTimeout(() => {
        if (this.mymap) {
          this.mymap.invalidateSize();
        }
      }, 100);
    } else if (this.currentStep.id === this.steps[3].id) {
      this.stepsComponent.setCurrentStep(this.steps[2]);
    }
  }

  async continueStep1() {
    this.isError = false;
    this.isErrorText = 'something-went-wrong';

    if (this.accountForm.get('email').invalid) {
      this.isError = true;
      this.isErrorText = 'email-is-not-valid';
      return;
    }

    if (this.accountForm.get('password').invalid) {
      this.isError = true;
      this.isErrorText = 'password-is-not-valid';
      return;
    }

    if (
      this.accountForm.get('password').value !==
      this.accountForm.get('confirmPassword').value
    ) {
      this.isError = true;
      this.isErrorText = 'passwords-are-not-equal';
      return;
    }

    if (this.accountForm.get('phone').invalid) {
      this.isError = true;
      this.isErrorText = 'form-is-not-valid';
      return;
    }

    this.registerService.data.email = this.accountForm.get('email').value;
    this.registerService.data.password = this.accountForm.get('password').value;
    this.registerService.data.phone = this.accountForm.get('phone').value;
    this.stepsComponent.setCurrentStep(this.steps[1]);
    this.getUserLocation();
  }

  async continueStep2() {
    this.isError = false;
    this.isErrorText = 'something-went-wrong';

    if (this.locationForm.get('display_name').invalid) {
      this.isError = true;
      this.isErrorText = 'form-is-not-valid';
      return;
    }

    if (this.locationForm.get('name').invalid) {
      this.isError = true;
      this.isErrorText = this.locationForm.get('name').hasError('pattern')
        ? 'business-name-is-not-valid'
        : 'form-is-not-valid';
      return;
    }

    if (
      this.locationForm.get('locationLatitude').invalid ||
      this.locationForm.get('locationLongitude').invalid
    ) {
      this.isError = true;
      this.isErrorText = 'select-a-location-in-the-map';
      return;
    }

    this.registerService.data.display_name =
      this.locationForm.get('display_name').value;
    this.registerService.data.name = this.locationForm.get('name').value;
    this.registerService.data.locationLatitude =
      this.locationForm.get('locationLatitude').value;
    this.registerService.data.locationLongitude =
      this.locationForm.get('locationLongitude').value;
    console.log('this.registerService.data:');
    console.log(this.registerService.data);
    this.stepsComponent.setCurrentStep(this.steps[2]);
  }

  async continueStep3() {
    this.isError = false;
    this.isErrorText = 'something-went-wrong';

    if (this.typeForm.get('selectedType').invalid) {
      this.isError = true;
      this.isErrorText = 'select-a-type-to-continue';
      return;
    }

    this.registerService.data.type = this.typeForm.get('selectedType').value;
    this.updatePlansForSelectedType();
    this.stepsComponent.setCurrentStep(this.steps[3]);
  }

  updatePlansForSelectedType() {
    if (this.selectedType && this.selectedType.id == 2) {
      this.filteredPricingPlans = this.pricingPlans.filter(
        (plan: any) => plan.id == 1,
      );
      this.setSelectedPlan(this.filteredPricingPlans[0]);
    } else {
      this.filteredPricingPlans = this.pricingPlans;
    }
  }

  async continueStep4() {
    this.isError = false;
    this.isErrorText = 'something-went-wrong';

    if (this.planForm.get('selectedPlan').invalid) {
      this.isError = true;
      this.isErrorText = 'select-a-plan-to-continue';
      return;
    }

    if (this.disableButton == true) {
      return;
    }

    this.disableButton = true;
    this.registerService.data.plan = this.planForm.get('selectedPlan').value;
    this.usersService.register(this.registerService.data).subscribe(
      async (response) => {
        console.log('response:');
        console.log(response);
        this.accountForm.reset();
        this.inputType1 = 'password';
        this.showHide1 = '../../../../assets/hide.svg';
        this.inputType2 = 'password';
        this.showHide2 = '../../../../assets/hide.svg';

        this.isError = false;
        this.disableButton = false;
        this.isErrorText = 'something-went-wrong';
        let modal = this.modalService.show(MessagePopupComponent, {
          initialState: {
            title: 'verify-email',
            text: 'verify-email-text',
            buttonText: 'okay',
          },
          class: 'alert-modal modal-dialog-centered',
          backdrop: 'static',
        });
        modal.onHidden.subscribe(() => {
          this.location.back();
        });
      },
      (error) => {
        console.log('error:');
        console.log(error);
        this.isError = true;
        this.disableButton = false;
        this.isErrorText = 'something-went-wrong';
      },
    );
  }

  async setupMap() {
    if (this.mymap) {
      this.mymap.remove();
    }

    this.mymap = L.map('map', {
      center: [
        this.locationForm.get('locationLatitude').value,
        this.locationForm.get('locationLongitude').value,
      ],
      zoom: 10,
      renderer: L.canvas(),
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      className: 'map-tiles',
    }).addTo(this.mymap);

    const searchForAddress =
      this.translateService.instant('search-for-address');

    const provider = new OpenStreetMapProvider();
    const searchControl = GeoSearchControl({
      provider: provider,
      style: 'bar',
      showMarker: false,
      showPopup: false,
      autoClose: true,
      retainZoomLevel: false,
      animateZoom: true,
      keepResult: true,
      searchLabel: searchForAddress,
    });
    this.mymap.addControl(searchControl);

    const markerIcon = L.icon({
      iconUrl: '../../../assets/map-marker.svg',
      iconSize: [30, 30],
    });

    this.marker = L.marker(
      [
        this.locationForm.get('locationLatitude').value,
        this.locationForm.get('locationLongitude').value,
      ],
      {
        icon: markerIcon,
        draggable: true,
      },
    ).addTo(this.mymap);

    this.mymap.on('click', (e) => {
      var latlng = e.latlng;

      this.locationForm.get('locationLatitude').setValue(latlng.lat);
      this.locationForm.get('locationLongitude').setValue(latlng.lng);

      this.marker.setLatLng([latlng.lat, latlng.lng]);
    });

    this.marker.on('dragend', (e) => {
      var latlng = e.target.getLatLng();

      this.locationForm.get('locationLatitude').setValue(latlng.lat);
      this.locationForm.get('locationLongitude').setValue(latlng.lng);
    });

    this.mymap.on('geosearch/showlocation', (e: any) => {
      const { location } = e;
      this.marker.setLatLng([location.y, location.x]);
      this.locationForm.get('locationLatitude').setValue(location.y);
      this.locationForm.get('locationLongitude').setValue(location.x);
    });

    setTimeout(() => {
      this.mymap.invalidateSize();
    }, 30000);
  }

  getUserLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          this.locationForm
            .get('locationLatitude')
            .setValue(position.coords.latitude);
          this.locationForm
            .get('locationLongitude')
            .setValue(position.coords.longitude);
          if (this.currentStep && this.currentStep.id == 2) {
            this.setupMap();
          }
        },
        (error: GeolocationPositionError) => {
          console.log('error:');
          console.log(error);
          this.locationForm.get('locationlatitude').setValue(42.6635);
          this.locationForm.get('locationlongitude').setValue(21.1638);
          if (this.currentStep && this.currentStep.id == 2) {
            this.setupMap();
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    } else {
      console.log('Geolocation is not supported by this browser.');
      this.locationForm.get('locationlatitude').setValue(42.6635);
      this.locationForm.get('locationlongitude').setValue(21.1638);
      if (this.currentStep && this.currentStep.id == 2) {
        this.setupMap();
      }
    }
  }
}
